import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useUIStore } from '../../stores/uiStore'
import { useReducedMotion } from '../../hooks/useReducedMotion'
import PlatformTabs from './PlatformTabs'
import PlatformTab from './PlatformTab'
import InfographicWorkspace from './InfographicWorkspace'
import ConfidenceBadge from './ConfidenceBadge'
import SourcesList from './SourcesList'
import type { SupportResponse } from '../../types'

interface ResponsePanelProps {
  response: SupportResponse
  responseTime?: number
}

export default function ResponsePanel({ response, responseTime }: ResponsePanelProps) {
  const { activeTab, setActiveTab } = useUIStore()
  const prefersReducedMotion = useReducedMotion()
  const [showInfographicWorkspace, setShowInfographicWorkspace] = useState(false)

  // Open workspace when infographic tab is clicked
  useEffect(() => {
    if (activeTab === 'infographic') {
      setShowInfographicWorkspace(true)
    }
  }, [activeTab])

  // Close workspace handler
  const handleCloseWorkspace = () => {
    setShowInfographicWorkspace(false)
    setActiveTab('twitter') // Return to first tab
  }

  const platformContent = {
    twitter: response.tweet,
    facebook: response.facebook,
    forum: response.forum,
  }

  // Animation variants for tab content
  const tabVariants = {
    initial: prefersReducedMotion ? { opacity: 1 } : { opacity: 0, x: 10 },
    animate: { opacity: 1, x: 0 },
    exit: prefersReducedMotion ? { opacity: 0 } : { opacity: 0, x: -10 },
  }

  return (
    <div className="space-y-6">
      {/* Header with confidence and response time */}
      <div className="flex items-center justify-between">
        <ConfidenceBadge
          level={response.confidence}
          rationale={response.confidenceRationale}
        />
        {responseTime && responseTime > 0 && (
          <span className="text-xs text-[var(--bbs-text-tertiary)]">
            Response time: {(responseTime / 1000).toFixed(2)}s
          </span>
        )}
      </div>

      {/* Platform tabs */}
      <PlatformTabs />

      {/* Response content */}
      <div className="card overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab === 'infographic' ? 'placeholder' : activeTab}
            variants={tabVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.15, ease: [0.23, 1, 0.32, 1] }}
          >
            {activeTab === 'infographic' ? (
              <div className="py-12 text-center text-[var(--bbs-text-secondary)]">
                Opening infographic workspace...
              </div>
            ) : (
              <PlatformTab
                platform={activeTab}
                content={platformContent[activeTab]}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Sources */}
      {response.sources.length > 0 && (
        <SourcesList sources={response.sources} />
      )}

      {/* Infographic Workspace Modal */}
      {showInfographicWorkspace && (
        <InfographicWorkspace
          infographic={response.infographic}
          onClose={handleCloseWorkspace}
        />
      )}
    </div>
  )
}
